import React, { useState, useEffect, useCallback } from "react";
import { Link } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import AdminLayout from "@/components/admin/AdminLayout";
import axios from "axios";
import {
  Users, UserCheck, UserX, AlertTriangle, Zap, Building2,
  Download, Search, Filter, Eye, RefreshCw, ArrowRight, MessageSquare, Phone, Copy, Check, Trash2
} from "lucide-react";
import { ADMIN } from "@/constants/testIds";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const AVA_HEADSHOT = "https://customer-assets.emergentagent.com/job_app-constructor-85/artifacts/ngv4sm7y_AvA%20Headshot.png";

const STATUS_COLORS = {
  "New Lead":                "bg-blue-50 text-blue-700 border-blue-100",
  "Qualified Clinic":        "bg-emerald-50 text-emerald-700 border-emerald-100",
  "Missing Provider":        "bg-amber-50 text-amber-700 border-amber-100",
  "Not a Fit":               "bg-red-50 text-red-600 border-red-100",
  "Live Team Handoff":       "bg-purple-50 text-purple-700 border-purple-100",
  "Qualified Vendor Partner":"bg-indigo-50 text-indigo-700 border-indigo-100",
  "Needs Review":            "bg-slate-100 text-slate-600 border-slate-200",
  "Compliance Review":       "bg-orange-50 text-orange-700 border-orange-100",
  "Pharmacy Pathway":        "bg-cyan-50 text-cyan-700 border-cyan-100",
  "Lab Pathway":             "bg-teal-50 text-teal-700 border-teal-100",
  "Telehealth Pathway":      "bg-violet-50 text-violet-700 border-violet-100",
};
const ALL_STATUSES = Object.keys(STATUS_COLORS);

const PIPELINE = [
  { label: "New Lead",          key: "new",              color: "bg-blue-500" },
  { label: "Needs Review",      key: "needs_review",     color: "bg-slate-400" },
  { label: "Qualified Clinic",  key: "qualified",        color: "bg-emerald-500" },
  { label: "Missing Provider",  key: "missing_provider", color: "bg-amber-500" },
  { label: "Live Handoff",      key: "live_handoff",     color: "bg-purple-500" },
];

function greeting() {
  const h = new Date().getHours();
  if (h < 12) return "Good morning";
  if (h < 17) return "Good afternoon";
  return "Good evening";
}

export default function AdminDashboard() {
  const { authHeader } = useAuth();

  const [data, setData] = useState({ leads: [], total: 0, stats: {} });
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [page, setPage] = useState(0);
  const [csvString, setCsvString] = useState("");
  const [csvCopied, setCsvCopied] = useState(false);
  const limit = 20;

  const buildCsvString = useCallback((leads) => {
    if (!leads?.length) { setCsvString(""); return; }
    const cols = ["id","first_name","last_name","email","phone","role","company_name",
      "website","city","state","clinic_types","has_licensed_provider","has_npi",
      "has_medical_director","license_types","requested_pathways","current_situation",
      "monthly_patient_volume","timeline_to_launch","lead_status","tags","source","created_at"];
    const esc = v => {
      if (v === null || v === undefined) return "";
      const s = Array.isArray(v) ? v.join("; ") : String(v);
      return (s.includes(",") || s.includes('"') || s.includes("\n")) ? `"${s.replace(/"/g,'""')}"` : s;
    };
    const csv = [cols.join(","), ...leads.map(l => cols.map(c => esc(l[c])).join(","))].join("\n");
    setCsvString(csv);
  }, []);

  const exportCSV = useCallback(async () => {
    if (!csvString) return;
    // Primary: clipboard copy (works in all modern browsers without download restrictions)
    try {
      await navigator.clipboard.writeText(csvString);
      setCsvCopied(true);
      setTimeout(() => setCsvCopied(false), 3000);
      return;
    } catch {
      // Fallback: data URI download
    }
    try {
      const blob = new Blob(["\ufeff" + csvString], { type: "text/csv;charset=utf-8;" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = "apexum_leads.csv";
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      setTimeout(() => URL.revokeObjectURL(url), 1000);
      setCsvCopied(true);
      setTimeout(() => setCsvCopied(false), 3000);
    } catch (err) {
      alert("Unable to export CSV automatically. Please try refreshing the page.");
    }
  }, [csvString]);

  const fetchLeads = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (search) params.append("search", search);
      if (statusFilter) params.append("status", statusFilter);
      params.append("skip", page * limit);
      params.append("limit", limit);
      const res = await axios.get(`${API}/leads?${params}`, { headers: authHeader() });
      setData(res.data);
    } catch {}
    finally { setLoading(false); }
  }, [search, statusFilter, page, authHeader]);

  // Also fetch all leads for CSV (separate from paginated view)
  const fetchAllForCsv = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/leads?limit=10000`, { headers: authHeader() });
      buildCsvString(res.data.leads || []);
    } catch {}
  }, [authHeader, buildCsvString]);

  useEffect(() => { fetchLeads(); }, [fetchLeads]);
  useEffect(() => { fetchAllForCsv(); }, [fetchAllForCsv]);

  const deleteLead = useCallback(async (id, name) => {
    if (!window.confirm(`Delete lead "${name}"? This cannot be undone.`)) return;
    try {
      await axios.delete(`${API}/leads/${id}`, { headers: authHeader() });
      fetchLeads();
      fetchAllForCsv();
    } catch {}
  }, [authHeader, fetchLeads, fetchAllForCsv]);

  const stats = data.stats || {};
  const total = stats.total || 0;
  const qualifiedPct = total > 0 ? Math.round(((stats.qualified || 0) / total) * 100) : 0;

  return (
    <AdminLayout title="Dashboard">
      <div data-testid={ADMIN.dashboard} className="space-y-5">

        {/* Welcome Hero Banner */}
        <div className="rounded-3xl bg-gradient-to-br from-blue-600 via-blue-500 to-cyan-500 p-6 shadow-xl shadow-blue-600/20 overflow-hidden relative">
          <div className="absolute top-0 right-0 w-64 h-64 bg-white/5 rounded-full -translate-y-1/2 translate-x-1/4" />
          <div className="absolute bottom-0 left-1/3 w-48 h-48 bg-cyan-400/10 rounded-full translate-y-1/2" />
          <div className="relative flex flex-col sm:flex-row items-start sm:items-center gap-5">
            <div className="w-14 h-14 rounded-full ring-2 ring-white/30 overflow-hidden flex-shrink-0 shadow-lg">
              <img src={AVA_HEADSHOT} alt="AvA" className="w-full h-full object-cover object-top" />
            </div>
            <div className="flex-1">
              <p className="text-white/70 text-sm mb-0.5">Apexum's Verified Access</p>
              <h2 className="text-2xl font-semibold text-white" style={{ fontFamily: 'Outfit, sans-serif' }}>
                {greeting()}, Lee
              </h2>
              <p className="text-white/60 text-sm mt-1">
                {total === 0 ? "No leads yet — share AvA to start collecting clinic information." :
                  `${total} total lead${total !== 1 ? "s" : ""} · ${stats.qualified || 0} qualified clinic${(stats.qualified || 0) !== 1 ? "s" : ""}`}
              </p>
            </div>
            <div className="flex gap-6 sm:ml-auto">
              <div className="text-center">
                <div className="text-3xl font-bold text-white" style={{ fontFamily: 'Outfit, sans-serif' }}>{total}</div>
                <div className="text-white/60 text-xs mt-0.5">Total Leads</div>
              </div>
              <div className="text-center">
                <div className="text-3xl font-bold text-white" style={{ fontFamily: 'Outfit, sans-serif' }}>{stats.qualified || 0}</div>
                <div className="text-white/60 text-xs mt-0.5">Qualified</div>
              </div>
              <div className="text-center">
                <div className="text-3xl font-bold text-white" style={{ fontFamily: 'Outfit, sans-serif' }}>{qualifiedPct}%</div>
                <div className="text-white/60 text-xs mt-0.5">Qual. Rate</div>
              </div>
            </div>
          </div>
        </div>

        {/* Stats Grid */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
          {[
            { icon: Zap, label: "New Leads", value: stats.new || 0, ring: "ring-blue-100", num: "text-blue-600", bg: "bg-blue-50", testId: ADMIN.statsNew },
            { icon: UserCheck, label: "Qualified Clinics", value: stats.qualified || 0, ring: "ring-emerald-100", num: "text-emerald-600", bg: "bg-emerald-50", testId: ADMIN.statsQualified },
            { icon: UserX, label: "Missing Provider", value: stats.missing_provider || 0, ring: "ring-amber-100", num: "text-amber-600", bg: "bg-amber-50", testId: null },
            { icon: Building2, label: "Vendor Partners", value: stats.vendor_partners || 0, ring: "ring-indigo-100", num: "text-indigo-600", bg: "bg-indigo-50", testId: null },
            { icon: AlertTriangle, label: "RUO Risk Concern", value: stats.ruo_risk || 0, ring: "ring-red-100", num: "text-red-600", bg: "bg-red-50", testId: null },
            { icon: Zap, label: "GLP-1 Interest", value: stats.glp1_interest || 0, ring: "ring-purple-100", num: "text-purple-600", bg: "bg-purple-50", testId: null },
            { icon: Users, label: "Live Handoffs", value: stats.live_handoff || 0, ring: "ring-orange-100", num: "text-orange-600", bg: "bg-orange-50", testId: null },
            { icon: Users, label: "Needs Review", value: stats.needs_review || 0, ring: "ring-slate-200", num: "text-slate-600", bg: "bg-slate-50", testId: null },
          ].map((card, i) => {
            const Icon = card.icon;
            return (
              <div key={i} className={`bg-white rounded-2xl border border-slate-100 shadow-[0_4px_20px_rgb(0,0,0,0.04)] p-5 ring-1 ${card.ring} hover:-translate-y-0.5 transition-transform duration-200`}>
                <div className="flex items-start justify-between mb-3">
                  <p className="text-xs font-medium text-slate-500">{card.label}</p>
                  <div className={`w-8 h-8 rounded-xl ${card.bg} flex items-center justify-center`}>
                    <Icon size={15} className={card.num} />
                  </div>
                </div>
                <p data-testid={card.testId} className={`text-3xl font-bold ${card.num}`} style={{ fontFamily: 'Outfit, sans-serif' }}>
                  {card.value}
                </p>
              </div>
            );
          })}
        </div>

        {/* Pipeline Bar */}
        {total > 0 && (
          <div className="bg-white rounded-2xl border border-slate-100 shadow-[0_4px_20px_rgb(0,0,0,0.04)] p-5">
            <h3 className="text-sm font-semibold text-slate-900 mb-4" style={{ fontFamily: 'Outfit, sans-serif' }}>Lead Pipeline</h3>
            <div className="flex rounded-full overflow-hidden h-3 mb-4 gap-0.5">
              {PIPELINE.map(p => {
                const count = p.key === "needs_review" ? (stats.needs_review || 0) : (stats[p.key] || 0);
                const pct = total > 0 ? (count / total) * 100 : 0;
                if (pct === 0) return null;
                return <div key={p.key} className={`${p.color} transition-all duration-500`} style={{ width: `${pct}%` }} title={`${p.label}: ${count}`} />;
              })}
            </div>
            <div className="flex flex-wrap gap-4">
              {PIPELINE.map(p => {
                const count = p.key === "needs_review" ? (stats.needs_review || 0) : (stats[p.key] || 0);
                return (
                  <div key={p.key} className="flex items-center gap-2 text-xs">
                    <div className={`w-2.5 h-2.5 rounded-full ${p.color}`} />
                    <span className="text-slate-600">{p.label}</span>
                    <span className="font-semibold text-slate-900">{count}</span>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* Quick Actions */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
          {[
            { icon: MessageSquare, label: "View AvA Sessions", to: "/admin/sessions", color: "text-blue-600 bg-blue-50 border-blue-100 hover:bg-blue-100" },
            { icon: Phone, label: "Call Requests", to: "/admin/sessions?tab=calls", color: "text-cyan-600 bg-cyan-50 border-cyan-100 hover:bg-cyan-100" },
            { icon: Building2, label: "Vendor Partners", to: "/admin/vendors", color: "text-indigo-600 bg-indigo-50 border-indigo-100 hover:bg-indigo-100" },
            { icon: csvCopied ? Check : Download, label: csvCopied ? "Copied to clipboard!" : "Export All Leads", action: exportCSV, color: csvCopied ? "text-emerald-600 bg-emerald-50 border-emerald-100 hover:bg-emerald-100" : "text-slate-600 bg-slate-50 border-slate-200 hover:bg-slate-100" },
          ].map((item, i) => {
            const Icon = item.icon;
            const inner = (
              <>
                <div className={`w-9 h-9 rounded-xl flex items-center justify-center flex-shrink-0 ${item.color.split(" ").slice(1, 3).join(" ")}`}>
                  <Icon size={16} className={item.color.split(" ")[0]} />
                </div>
                <span className="text-sm font-medium text-slate-700">{item.label}</span>
                <ArrowRight size={14} className="ml-auto text-slate-400" />
              </>
            );
            return item.to ? (
              <Link key={i} to={item.to} className={`flex items-center gap-3 p-4 rounded-2xl border ${item.color} transition-all duration-200`}>{inner}</Link>
            ) : (
              <button key={i} onClick={item.action} disabled={!csvString}
                className={`flex items-center gap-3 p-4 rounded-2xl border ${item.color} transition-all duration-200 text-left w-full disabled:opacity-40`}>{inner}</button>
            );
          })}
        </div>

        {/* Leads Table */}
        <div className="bg-white rounded-2xl border border-slate-100 shadow-[0_4px_20px_rgb(0,0,0,0.04)] overflow-hidden">
          {/* Table controls */}
          <div className="px-5 py-4 border-b border-slate-50 flex flex-col sm:flex-row gap-3 items-start sm:items-center justify-between">
            <h3 className="font-semibold text-slate-900 text-sm" style={{ fontFamily: 'Outfit, sans-serif' }}>
              Leads {data.total > 0 && <span className="text-slate-400 font-normal">· {data.total} total</span>}
            </h3>
            <div className="flex gap-2 flex-wrap">
              <div className="relative">
                <Search size={13} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                <input value={search} onChange={e => { setSearch(e.target.value); setPage(0); }}
                  placeholder="Search..."
                  className="pl-8 pr-3 py-2 text-xs border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500/20 w-44" />
              </div>
              <div className="relative">
                <Filter size={13} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                <select value={statusFilter} onChange={e => { setStatusFilter(e.target.value); setPage(0); }}
                  className="pl-8 pr-6 py-2 text-xs border border-slate-200 rounded-xl focus:outline-none bg-white appearance-none">
                  <option value="">All Statuses</option>
                  {ALL_STATUSES.map(s => <option key={s}>{s}</option>)}
                </select>
              </div>
              <button onClick={fetchLeads} className="p-2 text-slate-400 hover:text-slate-600 hover:bg-slate-50 rounded-xl border border-slate-200 transition-colors">
                <RefreshCw size={13} />
              </button>
              <button data-testid={ADMIN.exportCsvBtn} onClick={exportCSV} disabled={!csvString}
                className="flex items-center gap-1.5 px-3 py-2 text-xs border border-slate-200 rounded-xl text-slate-600 hover:bg-slate-50 transition-colors disabled:opacity-40">
                {csvCopied ? <Check size={13} className="text-emerald-500" /> : <Copy size={13} />}
                {csvCopied ? "Copied!" : "Copy CSV"}
              </button>
            </div>
          </div>

          <div className="overflow-x-auto">
            <table data-testid={ADMIN.leadsTable} className="w-full text-sm">
              <thead className="bg-slate-50/80 border-b border-slate-100">
                <tr>
                  {["Name", "Company", "Email", "Role", "Status", "Date", ""].map(h => (
                    <th key={h} className="text-left px-5 py-3 text-xs font-medium text-slate-400">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-50">
                {loading ? (
                  <tr><td colSpan={7} className="px-5 py-12 text-center">
                    <div className="flex items-center justify-center gap-2 text-slate-400 text-sm">
                      <div className="w-4 h-4 rounded-full border-2 border-blue-400 border-t-transparent animate-spin" />
                      Loading leads...
                    </div>
                  </td></tr>
                ) : data.leads.length === 0 ? (
                  <tr><td colSpan={7} className="px-5 py-16 text-center">
                    <div className="flex flex-col items-center gap-3">
                      <div className="w-12 h-12 rounded-2xl bg-blue-50 flex items-center justify-center">
                        <Users size={22} className="text-blue-400" />
                      </div>
                      <p className="text-sm text-slate-400">No leads yet.</p>
                      <Link to="/talk-to-ava" target="_blank"
                        className="text-xs text-blue-600 hover:text-blue-700 font-medium flex items-center gap-1">
                        Share AvA's intake link <ArrowRight size={12} />
                      </Link>
                    </div>
                  </td></tr>
                ) : data.leads.map(lead => (
                  <tr key={lead.id} data-testid={ADMIN.leadRow} className="hover:bg-slate-50/60 transition-colors group">
                    <td className="px-5 py-3.5 font-medium text-slate-900">
                      {lead.first_name || lead.last_name
                        ? `${lead.first_name || ""} ${lead.last_name || ""}`.trim()
                        : <span className="text-slate-300 italic text-xs">Unknown</span>}
                    </td>
                    <td className="px-5 py-3.5 text-slate-500 text-sm">{lead.company_name || "—"}</td>
                    <td className="px-5 py-3.5 text-slate-500 text-sm">{lead.email || "—"}</td>
                    <td className="px-5 py-3.5 text-slate-400 text-xs">{lead.role || "—"}</td>
                    <td className="px-5 py-3.5">
                      <span className={`inline-block px-2.5 py-1 rounded-full text-xs font-medium border ${STATUS_COLORS[lead.lead_status] || "bg-slate-100 text-slate-500 border-slate-200"}`}>
                        {lead.lead_status || "New Lead"}
                      </span>
                    </td>
                    <td className="px-5 py-3.5 text-slate-400 text-xs">
                      {lead.created_at ? new Date(lead.created_at).toLocaleDateString() : "—"}
                    </td>
                    <td className="px-5 py-3.5 text-right">
                      <div className="flex items-center justify-end gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                        <Link to={`/admin/leads/${lead.id}`}
                          className="inline-flex items-center gap-1 text-xs text-blue-500 hover:text-blue-700 font-medium px-2 py-1 rounded-lg hover:bg-blue-50 transition-colors">
                          <Eye size={12} /> View
                        </Link>
                        <button
                          onClick={() => deleteLead(lead.id, `${lead.first_name || ""} ${lead.last_name || ""}`.trim() || lead.company_name || "this lead")}
                          className="inline-flex items-center p-1.5 text-slate-300 hover:text-red-500 hover:bg-red-50 rounded-lg transition-colors"
                          title="Delete lead"
                        >
                          <Trash2 size={12} />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {data.total > limit && (
            <div className="px-5 py-3.5 border-t border-slate-50 flex items-center justify-between">
              <p className="text-xs text-slate-400">Showing {page * limit + 1}–{Math.min((page + 1) * limit, data.total)} of {data.total}</p>
              <div className="flex gap-2">
                <button onClick={() => setPage(p => Math.max(0, p - 1))} disabled={page === 0}
                  className="px-3 py-1.5 text-xs border border-slate-200 rounded-lg disabled:opacity-40 hover:bg-slate-50 transition-colors">Previous</button>
                <button onClick={() => setPage(p => p + 1)} disabled={(page + 1) * limit >= data.total}
                  className="px-3 py-1.5 text-xs border border-slate-200 rounded-lg disabled:opacity-40 hover:bg-slate-50 transition-colors">Next</button>
              </div>
            </div>
          )}
        </div>
      </div>
    </AdminLayout>
  );
}
